import React, {
    ChangeEvent,
    useCallback,
    useState,
    useEffect,
    useMemo
} from "react"
import { ExclusiveChoiceDropdown, SingleLineTextInput } from "dash-styled"
import {
    FilterableJsonField,
    hasMatchedPath,
    getApplicableMatchTypes,
    castToJsonFieldType
} from "../../../../../../Models/Client/JsonFieldFilter"
import { useDebounce } from "../../../../../../Hooks/useDebounce"
import { PredicateInputComponentProps } from "../../../../../Common/FilterComposer/FilterPredicate"
import {
    JsonFilter,
    JsonFilterMatchType,
    matchedTypeToString
} from "../../../../../../Models/API/JobModel"

interface JsonPredicateInputProps
    extends PredicateInputComponentProps<JsonFilter> {
    fields: FilterableJsonField[]
}

export function JsonPredicateInput({
    value,
    fields,
    onChange
}: JsonPredicateInputProps) {
    const [immediateValue, setImmediateValue] = useState(String(value.value))
    const fieldType = fields.find(f => hasMatchedPath(f, value))?.type
    const debouncedValue = useDebounce(
        immediateValue,
        fieldType === "boolean" ? 0 : 500
    )
    const typedDebouncedValue =
        fieldType === undefined
            ? undefined
            : castToJsonFieldType(debouncedValue, fieldType)
    const typedValue =
        fieldType === undefined
            ? undefined
            : castToJsonFieldType(value.value, fieldType)
    const matchTypes = useMemo<JsonFilterMatchType[]>(
        () =>
            fieldType === undefined ? [] : getApplicableMatchTypes(fieldType),
        [fieldType]
    )
    const operator = matchTypes.includes(value.matchType)
        ? value.matchType
        : matchTypes[0]
    const handleChangeField = useCallback(
        (index: number) => onChange({ ...value, path: fields[index].path }),
        [value, onChange, fields]
    )
    const handleChangeOperator = useCallback(
        (index: number) => {
            const matchType = matchTypes[index]
            if (matchType === "IS_NOT_EMPTY") {
                setImmediateValue("")
            }
            return onChange({ ...value, matchType })
        },
        [value, matchTypes, onChange]
    )
    const handleChangeValue = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => setImmediateValue(e.target.value),
        []
    )
    const valueInput = useMemo(() => {
        if (operator === "IS_NOT_EMPTY") {
            return null
        }

        if (fieldType === "string" || fieldType === "object") {
            return (
                <SingleLineTextInput
                    testId="filter-value-field-value"
                    type="text"
                    size="sm"
                    className="flex-grow"
                    value={immediateValue}
                    onChange={handleChangeValue}
                />
            )
        }

        if (fieldType === "number") {
            return (
                <SingleLineTextInput
                    testId="filter-value-field-value"
                    type="number"
                    size="sm"
                    className="flex-grow"
                    value={immediateValue}
                    onChange={handleChangeValue}
                />
            )
        }

        const booleanIndex = typedValue === true ? 0 : 1
        const handleChangeBoolean = (index: number) => {
            setImmediateValue(index === 0 ? "1" : "")
        }

        return (
            <ExclusiveChoiceDropdown
                testId="filter-value-field-value"
                size="sm"
                className="flex-grow"
                choices={["true", "false"]}
                selectedIndex={booleanIndex}
                fitContentWidth={true}
                onClick={handleChangeBoolean}
            />
        )
    }, [operator, fieldType, typedValue, immediateValue, handleChangeValue])

    useEffect(() => {
        if (
            !(
                (Number.isNaN(typedDebouncedValue) &&
                    Number.isNaN(typedValue)) ||
                typedDebouncedValue === typedValue
            )
        ) {
            onChange(
                typedDebouncedValue === undefined
                    ? undefined
                    : { ...value, value: typedDebouncedValue }
            )
        }
    }, [typedDebouncedValue, typedValue, value, onChange])

    return (
        <div
            className="flex items-center justify-between"
            data-testid="filter-value"
        >
            {fields.length > 1 ? (
                <ExclusiveChoiceDropdown
                    testId="filter-value-field-name"
                    size="sm"
                    className="mr-2 flex-grow"
                    choices={fields.map(field => field.label)}
                    selectedIndex={fields.findIndex(f =>
                        hasMatchedPath(f, value)
                    )}
                    fitContentWidth={true}
                    onClick={handleChangeField}
                />
            ) : fields.length > 0 ? (
                <span className="mr-2 whitespace-no-wrap">
                    {fields[0].label}
                </span>
            ) : null}
            <ExclusiveChoiceDropdown
                testId="filter-value-operator"
                size="sm"
                className={`flex-grow ${valueInput === null ? "" : "mr-2"}`}
                choices={matchTypes}
                renderChoice={matchedTypeToString}
                selectedIndex={matchTypes.findIndex(o => o === operator)}
                fitContentWidth={true}
                onClick={handleChangeOperator}
            />
            {valueInput}
        </div>
    )
}
